import React, { useEffect, useRef } from 'react'
import { AiOutlineClose } from 'react-icons/ai'
import { MdSend, MdOutlineRefresh } from 'react-icons/md'
import { socketConnection } from '../../../_helpers'
import BotFlowStepsView from './BotFlowStepsView'
import moment from 'moment'
import { contactUsListActions } from '../../../_actions/contactUs.actions'
import { alertActions } from '../../../_actions/alert.actions'
import { useDispatch } from 'react-redux'

const CONSTANTS = {
  SUPPORT_CHAT_RELOAD: 'support_chat_reload'
}

const ChatPopup = ({
  openModule,
  closeMsgPopup,
  chatWindowRef,
  handleSendMessage,
  messages,
  inputValue,
  setInputValue,
  error,
  handleRefresh,
  prefillData,
  selectedMsg,
  setSelectedMsg,
  isSending = false,
  isLoading = false
}) => {
  const dispatch = useDispatch()
  const messagesEndRef = useRef(null)
  const isClosed = selectedMsg?.status?.toLowerCase() === 'closed'

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleInputChange = e => {
    setInputValue(e.target.value)
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && inputValue.trim().length >= 2 && !isClosed && !isSending) {
      handleSendMessage()
    }
  }

  const handleReopenTicketAdmin = () => {
    if (!selectedMsg?._id) return
    contactUsListActions
      .ticketStatusUpdate({ ticketId: selectedMsg._id, status: 'open' })
      .then(res => {
        dispatch(alertActions.success(res?.message || 'Ticket reopened successfully.'))
        if (setSelectedMsg) {
          setSelectedMsg(prev => ({ ...prev, status: 'open' }))
        }
        socketConnection().emit(CONSTANTS.SUPPORT_CHAT_RELOAD, { ticketId: selectedMsg._id })
        handleRefresh()
      })
      .catch(err => {
        dispatch(alertActions.error(err))
      })
  }

  const isInputValid = inputValue.trim().length >= 2

  return (
    <div>
      {openModule && (
        <div className='fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 backdrop-blur-sm'>
          <div className='lg:w-[600px] w-11/12 bg-white rounded-lg shadow-lg'>
            {/* Header */}
            <div className='bg-slate-800 text-white px-4 py-3 flex justify-between items-center rounded-t-lg'>
              <div className='flex flex-col'>
                <h2 className='text-lg font-semibold'>Chat Support {selectedMsg?.ticketId ? `(#${selectedMsg.ticketId})` : ''}</h2>
              </div>

              <div className='flex justify-end gap-4'>
                {/* Refresh Button */}
                <div className='relative group'>
                  <button
                    aria-label='Refresh chat'
                    className='text-white hover:text-gray-400 transition'
                    onClick={handleRefresh}
                  >
                    <MdOutlineRefresh size={25} />
                  </button>
                  <span className='absolute left-1/2 transform -translate-x-1/2 -translate-y-full mb-1 w-max bg-gray-500 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
                    Refresh Messages
                  </span>
                </div>

                {/* Close Button */}
                <div className='relative group'>
                  <button
                    className='text-white hover:text-gray-400 transition'
                    onClick={closeMsgPopup}
                    aria-label='Close chat'
                  >
                    <AiOutlineClose size={22} />
                  </button>
                  <span className='absolute left-1/2 transform -translate-x-1/2 -translate-y-full mb-1 w-max bg-gray-500 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
                    Close the Chat
                  </span>
                </div>
              </div>
            </div>

            {/* Chat Window */}
            <div
              className='p-4 text-sm text-gray-800 space-y-4 h-80 overflow-y-auto'
              ref={chatWindowRef}
              aria-label='Chat messages'
            >
              {isLoading && <p role='status'>Loading messages...</p>}
              {prefillData?.length > 0 && <BotFlowStepsView steps={prefillData}/>}
              {Array.isArray(messages) &&
                messages.length > 0 &&
                messages.map((ele, index) => (
                  <div
                    key={ele._id || index}
                    className={`flex ${ele.sender === 'SUPPORT' ? 'justify-end' : 'justify-start'
                      }`}
                  >
                    <div className="flex flex-col max-w-[75%]">
                      <span
                        className={`text-xs font-medium mb-1 ${
                          ele.sender === "SUPPORT"
                            ? "text-[#146F44]"
                            : "text-gray-500"
                        }`}
                      >
                        {ele.sender === "SUPPORT" ? "You" : "User"}
                      </span>
                      <div
                        className={`p-4 rounded-lg text-sm shadow-md ${
                          ele.sender === "SUPPORT"
                            ? "bg-[#00811D1A] text-[#0F172A]"
                            : "bg-[#E9E9E980] text-[#3D4260]"
                        }`}
                      >
                        {ele.messageType === "image" ? (
                          <img
                            src={ele.message}
                            className="rounded-md max-w-full h-auto"
                            alt="Message"
                          />
                        ) : (
                          <p>{ele.message}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 mt-1">
                        {ele.sentAt
                          ? moment(ele.sentAt).format("DD.MM.YYYY hh:mm A")
                          : "Just now"}
                      </span>
                    </div>
                  </div>
                ))}
              <div ref={messagesEndRef} />
            </div>

            {isClosed && (
              <div className='px-4 pt-3 pb-1 bg-slate-800 border-t border-slate-700'>
                <div className='p-2.5 bg-amber-500/20 border border-amber-500/40 rounded-lg text-amber-200 text-xs font-medium flex items-center justify-between gap-2'>
                  <span className='flex items-center gap-1.5'>🔒 This conversation has been closed.</span>
                  <button
                    type='button'
                    onClick={handleReopenTicketAdmin}
                    className='px-3 py-1 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 transition cursor-pointer'
                  >
                    Reopen Ticket
                  </button>
                </div>
              </div>
            )}

            {/* Message Input */}
            <div className='p-4 bg-slate-800 rounded-b-lg'>
              <div className='flex items-center'>
                <input
                  type='text'
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  disabled={isClosed || isSending}
                  placeholder={isClosed ? 'This conversation has been closed.' : 'Type your message here...'}
                  className={`w-full text-gray-900 p-3 rounded-lg outline-none ${
                    isClosed
                      ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                      : 'bg-gray-100 focus:ring-2 focus:ring-green-600'
                  }`}
                />
                <button
                  aria-label='Send message'
                  onClick={handleSendMessage}
                  disabled={!isInputValid || isClosed || isSending}
                  className={`ml-4 flex text-gray-900 bg-gray-800 border border-gray-300 focus:outline-none font-medium rounded-full text-sm items-center p-3 me-2
        ${!isInputValid || isClosed || isSending
                      ? 'text-gray-500 bg-gray-600 cursor-not-allowed'
                      : 'text-white bg-green-600 hover:bg-green-700 hover:scale-105'
                    } 
        active:scale-95`}
                >
                  <MdSend size={25} className='text-white' />
                </button>
              </div>
              {error && <p className='mt-2 text-red-500'>{error}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatPopup
